'use client';

import { Star } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type KeyboardEvent, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface StarRatingPickerProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const STAR_VALUES = [1, 2, 3, 4, 5] as const;

export function StarRatingPicker({ value, onChange, disabled = false }: StarRatingPickerProps) {
  const t = useTranslations('mealRating');
  const [hoverValue, setHoverValue] = useState(0);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusedIndex = value === 0 ? 0 : value - 1;
  const displayValue = hoverValue || value;

  const moveFocus = (nextValue: number) => {
    onChange(nextValue);
    buttonRefs.current[nextValue - 1]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(Math.min((value || 0) + 1, 5));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(Math.max((value || 1) - 1, 1));
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(5);
        break;
    }
  };

  return (
    <div
      className="flex"
      role="radiogroup"
      aria-label={t('prompt')}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => setHoverValue(0)}
    >
      {STAR_VALUES.map((starValue, index) => {
        const isFilled = starValue <= displayValue;
        const isFocusable = index === focusedIndex;

        return (
          <button
            key={starValue}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={value === starValue}
            aria-label={t(`starLabels.${starValue}`)}
            tabIndex={isFocusable ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(starValue)}
            onMouseEnter={() => setHoverValue(starValue)}
            className={cn(
              'flex min-h-[36px] min-w-[30px] cursor-pointer items-center justify-center rounded-full',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none',
            )}
          >
            <Star
              className={cn(
                'h-6 w-6 transition-all duration-150 active:scale-110',
                isFilled
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'fill-transparent text-muted-foreground/40',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
